import React, { useEffect, useState } from "react";

import { useFetchBiBreakdownsQuery, useFetchBiProductsQuery, useFetchBiVariantsQuery } from "../../../api/analyticsApi";
import { Chip, Select, Segmented, TextInput } from "../../catalogProductStudio/edit/StudioUi";
import { BarList, DataState, DataTable, ExportButton, Pager, Panel, ProductCell } from "../AnalyticsUi";
import { familyLabel, fill, formatDate, formatMoney, formatNumber, formatPercent } from "../analyticsModel";

const MIN_UNITS = 3;

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/** Profit/margin cell that says why a value is missing instead of showing 0. */
const CostCell = ({ cost, field, unitsSold, tt, lang }) => {
  if (!cost) return <span className="an-muted">—</span>;
  if (cost.status === "none") return <span className="an-muted">—</span>;
  if (cost.status === "unavailable") return <Chip tone="danger">{tt("costMissingTag")}</Chip>;
  const value = field === "grossMargin" ? formatPercent(cost.grossMargin, lang) : formatMoney(cost.grossProfit, lang);
  return (
    <span className="an-cell-stack is-end">
      <span>{value}</span>
      {cost.status === "partial" ? <Chip tone="warning">{tt("costIncompleteTag")}</Chip> : null}
      {cost.basis && cost.basis !== "recorded" ? <span className="an-muted" title={tt(`basis_${cost.basis}`)}>{tt(`basisShort_${cost.basis}`)}</span> : null}
      {field === "grossMargin" && cost.status === "complete" && unitsSold < MIN_UNITS ? <Chip>{tt("lowVolumeTag")}</Chip> : null}
    </span>
  );
};

export const productColumns = ({ tt, lang, costVisible, withLastSold = true }) => [
  {
    key: "product",
    label: tt("col_product"),
    sortKey: "title",
    className: "an-col-product",
    render: (row) => (
      <ProductCell
        tt={tt}
        productId={row.productId}
        imageUrl={row.imageUrl}
        title={row.title}
        sub={[row.categoryName || tt("uncategorised"), row.brandName].filter(Boolean).join(" · ")}
      />
    ),
  },
  { key: "units", label: tt("col_netUnits"), sortKey: "netUnits", align: "end", render: (row) => (
    <span className="an-cell-stack is-end">
      <strong>{formatNumber(row.netUnits, lang)}</strong>
      {row.unitsReturned ? <span className="an-muted">{row.unitsSold} − {row.unitsReturned}</span> : null}
    </span>
  ) },
  { key: "net", label: tt("col_netRevenue"), sortKey: "netRevenue", align: "end", render: (row) => (
    <span className="an-cell-stack is-end">
      <strong>{formatMoney(row.netRevenue, lang)}</strong>
      {row.discounts ? <span className="an-muted">−{formatMoney(row.discounts, lang)} {tt("discounts").toLowerCase()}</span> : null}
    </span>
  ) },
  ...(costVisible
    ? [
        { key: "profit", label: tt("col_profit"), sortKey: "grossProfit", align: "end", help: tt("kpi_grossProfit_help"), render: (row) => <CostCell cost={row.cost} field="grossProfit" unitsSold={row.unitsSold} tt={tt} lang={lang} /> },
        { key: "margin", label: tt("col_margin"), sortKey: "grossMargin", align: "end", render: (row) => <CostCell cost={row.cost} field="grossMargin" unitsSold={row.unitsSold} tt={tt} lang={lang} /> },
      ]
    : []),
  { key: "returns", label: tt("col_returnRate"), sortKey: "returnRate", align: "end", help: tt("returnRateHelp"), render: (row) =>
      row.returnRate === null ? <span className="an-muted">—</span> : (
        <span className="an-cell-stack is-end">
          <span>{formatPercent(row.returnRate, lang)}</span>
          {row.unitsSold < MIN_UNITS ? <span className="an-muted">{tt("lowVolumeTag")}</span> : null}
        </span>
      ) },
  { key: "sellThrough", label: tt("col_sellThrough"), sortKey: "sellThrough", align: "end", help: tt("sellThroughHelp"), render: (row) => formatPercent(row.sellThrough, lang) },
  { key: "onHand", label: tt("col_onHand"), sortKey: "onHand", align: "end", render: (row) => formatNumber(row.onHand, lang) },
  ...(withLastSold
    ? [{ key: "last", label: tt("col_lastSold"), align: "end", render: (row) => (row.lastSoldAt ? formatDate(row.lastSoldAt, lang, { day: "2-digit", month: "short" }) : <span className="an-muted">—</span>) }]
    : []),
];

const ProductsTable = ({ range, tt, lang, costVisible }) => {
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [family, setFamily] = useState("");
  const [performance, setPerformance] = useState("all");
  const [sort, setSort] = useState("netRevenue");
  const [direction, setDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  useEffect(() => setPage(1), [debouncedSearch, categoryId, family, performance, sort, direction, range.dateFrom, range.dateTo]);

  const filters = {
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    search: debouncedSearch || undefined,
    categoryId: categoryId || undefined,
    family: family || undefined,
    performance: performance === "all" ? undefined : performance,
  };
  const query = useFetchBiProductsQuery({ ...filters, sort, direction, page, pageSize: 25 });
  const data = query.data;

  const onSort = (key) => {
    if (key === sort) setDirection(direction === "desc" ? "asc" : "desc");
    else {
      setSort(key);
      setDirection(key === "title" ? "asc" : "desc");
    }
  };

  return (
    <Panel
      id="an-products"
      title={tt("productsTitle")}
      hint={fill(tt("productsHint"), { min: MIN_UNITS })}
      actions={<ExportButton dataset="products" params={filters} tt={tt} />}
    >
      <div className="an-filters">
        <TextInput className="an-filters__search" value={search} onChange={setSearch} placeholder={tt("search")} aria-label={tt("search")} type="search" />
        <Select value={categoryId} onChange={setCategoryId} aria-label={tt("category")}>
          <option value="">{tt("category")}: {tt("all")}</option>
          {(data?.filters.categories || []).map((category) => (
            <option key={category.key} value={category.key}>
              {category.label || tt("uncategorised")}
            </option>
          ))}
        </Select>
        <Select value={family} onChange={setFamily} aria-label={tt("family")}>
          <option value="">{tt("family")}: {tt("all")}</option>
          {(data?.filters.families || []).map((value) => (
            <option key={value} value={value}>
              {familyLabel(tt, value)}
            </option>
          ))}
        </Select>
        <Select value={performance} onChange={setPerformance} aria-label={tt("performance")}>
          {["all", "selling", "no_sales"].map((value) => (
            <option key={value} value={value}>
              {tt(`performance_${value}`)}
            </option>
          ))}
        </Select>
        <Select value={sort} onChange={setSort} aria-label={tt("sort")}>
          {["netRevenue", "netUnits", ...(costVisible ? ["grossProfit", "grossMargin"] : []), "returnRate", "sellThrough", "onHand", "title"].map((value) => (
            <option key={value} value={value}>
              {tt("sort")}: {tt(`sort_${value}`)}
            </option>
          ))}
        </Select>
        <Segmented
          ariaLabel={tt("sort")}
          value={direction}
          onChange={setDirection}
          options={[
            { value: "desc", label: tt("direction_desc") },
            { value: "asc", label: tt("direction_asc") },
          ]}
        />
      </div>
      <DataState query={query} tt={tt}>
        {data ? (
          <>
            <p className="an-muted an-small">{fill(tt("productsSummary"), { selling: data.summary.selling, none: data.summary.withoutSales })}</p>
            <DataTable
              caption={tt("productsTitle")}
              rows={data.items}
              rowKey={(row) => row.productId}
              sort={sort}
              direction={direction}
              onSort={onSort}
              emptyText={tt("emptyTitle")}
              columns={productColumns({ tt, lang, costVisible })}
            />
            <Pager pagination={data.pagination} onPage={setPage} tt={tt} />
          </>
        ) : null}
      </DataState>
    </Panel>
  );
};

const Breakdowns = ({ range, tt, lang }) => {
  const query = useFetchBiBreakdownsQuery({ dateFrom: range.dateFrom, dateTo: range.dateTo });
  const [view, setView] = useState("categories");
  const data = query.data;
  const dimensionRows = data ? data[view] : [];

  return (
    <Panel
      id="an-breakdowns"
      title={tt("breakdownsTitle")}
      actions={
        <>
          <Segmented
            ariaLabel={tt("breakdownsTitle")}
            value={view}
            onChange={setView}
            options={[
              { value: "categories", label: tt("categories") },
              { value: "sizes", label: tt("sizes") },
              { value: "colors", label: tt("colors") },
              { value: "families", label: tt("families") },
            ]}
          />
          <ExportButton dataset="categories" params={{ dateFrom: range.dateFrom, dateTo: range.dateTo }} tt={tt} />
        </>
      }
    >
      <DataState query={query} tt={tt}>
        {data ? (
          view === "categories" ? (
            <DataTable
              caption={tt("categories")}
              rows={data.categories}
              rowKey={(row) => row.key}
              emptyText={tt("emptyTitle")}
              columns={[
                { key: "label", label: tt("category"), render: (row) => row.label || tt("uncategorised") },
                { key: "units", label: tt("col_netUnits"), align: "end", render: (row) => formatNumber(row.netUnits, lang) },
                { key: "net", label: tt("col_netRevenue"), align: "end", render: (row) => <strong>{formatMoney(row.netRevenue, lang)}</strong> },
                { key: "share", label: tt("share"), align: "end", render: (row) => formatPercent(row.share, lang) },
                { key: "change", label: tt("change"), align: "end", render: (row) => (row.change === null ? <span className="an-muted">—</span> : `${row.change > 0 ? "+" : ""}${formatPercent(row.change, lang)}`) },
                ...(data.access.costVisible
                  ? [{ key: "profit", label: tt("col_profit"), align: "end", render: (row) => <CostCell cost={row.cost} field="grossProfit" unitsSold={row.unitsSold} tt={tt} lang={lang} /> }]
                  : []),
                { key: "returns", label: tt("col_returnRate"), align: "end", render: (row) => formatPercent(row.returnRate, lang) },
                { key: "onHand", label: tt("col_onHand"), align: "end", render: (row) => formatNumber(row.onHand, lang) },
              ]}
            />
          ) : (
            <BarList
              rows={dimensionRows.map((row) => ({ ...row, key: row.key }))}
              valueKey={view === "families" ? "netRevenue" : "netUnits"}
              labelOf={(row) => (row.label ? (view === "families" ? familyLabel(tt, row.label) : row.label) : tt("notSet"))}
              format={(value, row) =>
                view === "families"
                  ? `${formatMoney(row.netRevenue, lang)} · ${formatNumber(row.unitsSold - row.unitsReturned, lang)} ${tt("units").toLowerCase()}`
                  : `${formatNumber(value, lang)} ${tt("units").toLowerCase()} · ${formatMoney(row.netRevenue, lang)}`
              }
              secondary={(row) =>
                `${tt("col_onHand")}: ${formatNumber(row.onHand, lang)}${row.returnRate !== null && row.returnRate !== undefined ? ` · ${tt("col_returnRate")}: ${formatPercent(row.returnRate, lang)}` : ""}`
              }
              emptyText={tt("emptyTitle")}
            />
          )
        ) : null}
      </DataState>
    </Panel>
  );
};

const VariantsTable = ({ range, tt, lang, costVisible }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);
  useEffect(() => setPage(1), [debouncedSearch, range.dateFrom, range.dateTo]);
  const filters = { dateFrom: range.dateFrom, dateTo: range.dateTo, search: debouncedSearch || undefined };
  const query = useFetchBiVariantsQuery({ ...filters, page, pageSize: 25 });

  return (
    <Panel
      id="an-variants"
      title={tt("variantsTitle")}
      hint={tt("variantsHint")}
      actions={<ExportButton dataset="variants" params={filters} tt={tt} />}
    >
      <div className="an-filters">
        <TextInput className="an-filters__search" value={search} onChange={setSearch} placeholder={tt("search")} aria-label={tt("search")} type="search" />
      </div>
      <DataState query={query} tt={tt}>
        {query.data ? (
          <>
            <DataTable
              caption={tt("variantsTitle")}
              rows={query.data.items}
              rowKey={(row) => row.variantId}
              emptyText={tt("emptyTitle")}
              columns={[
                {
                  key: "variant",
                  label: tt("col_variant"),
                  className: "an-col-product",
                  render: (row) => (
                    <ProductCell
                      tt={tt}
                      productId={row.productId}
                      imageUrl={row.imageUrl}
                      title={row.productTitle}
                      sub={[row.color, row.size, row.sku].filter(Boolean).join(" · ")}
                    />
                  ),
                },
                { key: "units", label: tt("col_netUnits"), align: "end", render: (row) => formatNumber(row.netUnits, lang) },
                { key: "net", label: tt("col_netRevenue"), align: "end", render: (row) => <strong>{formatMoney(row.netRevenue, lang)}</strong> },
                ...(costVisible
                  ? [{ key: "profit", label: tt("col_profit"), align: "end", render: (row) => <CostCell cost={row.cost} field="grossProfit" unitsSold={row.unitsSold} tt={tt} lang={lang} /> }]
                  : []),
                { key: "returns", label: tt("col_returnRate"), align: "end", render: (row) => formatPercent(row.returnRate, lang) },
                { key: "sellThrough", label: tt("col_sellThrough"), align: "end", render: (row) => formatPercent(row.sellThrough, lang) },
                { key: "onHand", label: tt("col_onHand"), align: "end", render: (row) => formatNumber(row.onHand, lang) },
              ]}
            />
            <Pager pagination={query.data.pagination} onPage={setPage} tt={tt} />
          </>
        ) : null}
      </DataState>
    </Panel>
  );
};

const ProductsSection = (props) => (
  <>
    <ProductsTable {...props} />
    <Breakdowns {...props} />
    <VariantsTable {...props} />
  </>
);

export default ProductsSection;
